import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { showToast } from '@/globalFunction';
import { getRegisteredMessagingStore } from '../myConversations/store/messaging.store';
import { queryClient } from '@/lib/react-query/react-query';
import {
  messagingQueryKeys,
  MESSAGING_API_BASE,
} from '../myConversations/api/config';
import { axios } from '@/lib/axios/axios';
import {
  updateChannelInCache,
  addNewChannelToCache,
  removeChannelFromCache,
  updateChannelLastMessageAt,
} from '../myConversations/api/utils/updateChannelCache';

@connect((store) => {
  return {
    socket: store.socket.container,
    loggedUser: store.loggedUser,
    globalConfig: store.globalConfig,
    project: store.project,
  };
})
export default class Socket extends React.Component {
  constructor(props) {
    super(props);
    this.getCurrentProps = this.getCurrentProps.bind(this);
  }
  UNSAFE_componentWillMount() {
    const { socket, loggedUser, dispatch } = this.props;

    // console.log('[Socket] Global socket handlers mounting - socket connected:', socket?.connected);

    // Remove any existing listeners to prevent duplicates (in case of remount/hot reload)
    socket.off('FRONT_TIMELOG_SETTING');
    socket.off('FRONT_PROJECT_UPDATE');
    socket.off('REPORT_UPDATE_UPDATE');
    socket.off('MESSAGING_NEW_MESSAGE');
    socket.off('MESSAGING_MESSAGE_EDITED');
    socket.off('MESSAGING_MESSAGE_DELETED');
    socket.off('MESSAGING_UNREAD_COUNT');

    // console.log('[Socket] Registering MESSAGING_* handlers...');

    socket.on('FRONT_TIMELOG_SETTING', (data) => {
      const group = _.groupBy(data, 'name');
      // console.log("group1",group)
      dispatch({ type: 'SET_GLOBAL_CONFIG_LIST', global: group });
    });

    socket.on('FRONT_PROJECT_UPDATE', (data) => {
      const { project } = this.getCurrentProps();
      if (
        project.Selected &&
        _.isEqual(project.Selected.id, data.id) &&
        !_.isEqual(project.Selected.requiredAction, data.requiredAction)
      ) {
        dispatch({
          type: 'SET_PROJECT_SELECTED',
          Selected: {
            ...project.Selected,
            requiredAction: data.requiredAction,
          },
        });
      }
    });
    socket.on('REPORT_UPDATE_UPDATE', (data) => {
      const { loggedUser } = this.getCurrentProps();
      // console.log("REPORT_UPDATE_UPDATE",data)
      // Existing code...
      if (_.isEqual(loggedUser.data.id, data.userId)) {
        // if(data.status) {
        //     showToast("success", `${data.filename} sent to ${loggedUser.data.emailAddress}`);
        // } else {
        //     showToast("error", "Something went wrong. Please try again later.");
        // }
      }
    });

    // Listen for messaging new message events to update the shared messaging store
    socket.on('MESSAGING_NEW_MESSAGE', async (data) => {
      try {
        // console.log('[Socket] MESSAGING_NEW_MESSAGE received:', data);

        // Check if this is an incoming message (not from current user)
        const { loggedUser } = this.getCurrentProps();
        const currentUserId = loggedUser?.data?.id;
        const senderId =
          data.senderId ||
          (data.message && data.message['sender-user-id']) ||
          null;
        const isIncoming =
          currentUserId &&
          senderId &&
          String(currentUserId) !== String(senderId);

        // console.log('[Socket] User ID:', currentUserId, 'Sender ID:', senderId, 'Is incoming:', isIncoming);

        // console.log('[Socket] About to update messaging store and channel cache...');

        const messagingStore =
          getRegisteredMessagingStore && getRegisteredMessagingStore();
        // console.log('[Socket] messagingStore exists:', !!messagingStore);
        if (messagingStore) {
          const state = messagingStore.getState();
          if (state && typeof state.setSocketData === 'function') {
            // Only mark channel for refetch if this is an INCOMING message
            // Own messages don't need refetch (already in cache via optimistic update)
            if (isIncoming) {
              state.setSocketData((prev) => ({
                newMessages: [...(prev.newMessages || []), data.channelId],
              }));
              // console.log('[Socket] Updated socketData.newMessages with channel', data.channelId);
            }
          }
        }

        // console.log('[Socket] Starting message cache upsert for channel', data.channelId);

        // Don't increment unread count here - backend sends MESSAGING_UNREAD_COUNT event
        // with the canonical count, which is handled in useMessagingSubscriptions
        // Manual increments can cause race conditions resulting in double-counting

        // Upsert minimal incoming message into cached messages queries for this channel
        try {
          const channelId = Number(data.channelId);
          // console.log('[Socket] Parsed channelId:', channelId, 'isNaN:', isNaN(channelId));
          if (!isNaN(channelId)) {
            // Build a minimal normalized message object
            const incoming = data.message
              ? { ...data.message }
              : {
                  id: data.messageId,
                  'message-id': data.messageId,
                  'channel-id': channelId,
                  'sender-user-id':
                    data.senderId ||
                    (data.message && data.message['sender-user-id']) ||
                    null,
                  'date-added':
                    (data.message &&
                      (data.message['date-added'] || data.message.createdAt)) ||
                    new Date().toISOString(),
                  content:
                    (data.message &&
                      (data.message.content || data.message.text)) ||
                    data.content ||
                    '',
                };

            // Ensure there's a stable id field if backend uses id or message-id
            if (!incoming.id && incoming['message-id'])
              incoming.id = incoming['message-id'];

            // Helper to compute stable key for deduping
            const messageKey = (m) => {
              const id = m?.id ?? m?.['message-id'];
              if (id !== undefined && id !== null) return String(id);
              return (
                String(m?.['date-added'] ?? m?.createdAt ?? '') +
                '::' +
                String(m?.['sender-user-id'] ?? m?.senderId ?? '') +
                '::' +
                String(
                  (m?.content && m.content.body?.slice?.(0, 40)) ||
                    m?.text ||
                    ''
                )
              );
            };

            const allMessageQueries =
              queryClient.getQueriesData?.({
                predicate: (query) => {
                  const key = query.queryKey || [];
                  return (
                    key?.[0] === 'messaging' &&
                    key.some((k) => k === 'messages') &&
                    key.some((k) => k === channelId)
                  );
                },
              }) || [];

            allMessageQueries.forEach(([qk, qd]) => {
              try {
                // Handle infinite query shape ({ pages: [...] })
                if (qd && qd.pages) {
                  // Build set of existing keys
                  const existingKeys = new Set();
                  qd.pages
                    .flat()
                    .forEach((m) => existingKeys.add(messageKey(m)));

                  const incomingKey = messageKey(incoming);
                  if (!existingKeys.has(incomingKey)) {
                    // Append to last page (assuming pages are chronological)
                    const newPages = qd.pages.slice();
                    const lastIdx = newPages.length - 1;
                    if (lastIdx >= 0) {
                      newPages[lastIdx] = [...newPages[lastIdx], incoming];
                    } else {
                      newPages.push([incoming]);
                    }
                    queryClient.setQueryData(qk, {
                      ...qd,
                      pages: newPages,
                    });
                  }
                } else if (Array.isArray(qd)) {
                  // If data is a plain array, upsert there
                  const existingKeys = new Set(qd.map((m) => messageKey(m)));
                  const incomingKey = messageKey(incoming);
                  if (!existingKeys.has(incomingKey)) {
                    queryClient.setQueryData(qk, [...qd, incoming]);
                  }
                }
              } catch (e) {
                console.error(
                  '[Socket] Error during per-query message upsert:',
                  e
                );
              }
            });

            // console.log('[Socket] Message upsert complete, now checking channel list cache...');

            // Check if channel exists in channels list cache
            const channelsListQueries = queryClient
              .getQueryCache()
              .getAll()
              .filter((query) => {
                const key = query.queryKey || [];
                return (
                  Array.isArray(key) &&
                  key.length >= 2 &&
                  key[0] === 'messaging' &&
                  key[1] === 'channels'
                );
              });

            // console.log('[Socket] Found', channelsListQueries.length, 'channel list queries in cache');

            let channelExistsInList = false;
            if (channelsListQueries.length > 0) {
              for (const query of channelsListQueries) {
                const queryData = query.state.data;
                if (queryData && queryData.pages) {
                  for (const page of queryData.pages) {
                    if (
                      page.rows &&
                      page.rows.some(
                        (ch) => String(ch.id) === String(channelId)
                      )
                    ) {
                      channelExistsInList = true;
                      // console.log('[Socket] Channel', channelId, 'found in cache');
                      break;
                    }
                  }
                }
                if (channelExistsInList) break;
              }
            }

            const messageTimestamp =
              incoming['date-added'] ||
              incoming['dateAdded'] ||
              new Date().toISOString();

            if (channelsListQueries.length === 0) {
              // No channel list queries in cache - admin is likely away
              // Invalidate channels so they refetch when user returns
              // console.log('[Socket] No channel list queries - invalidating so they refetch on return');
              queryClient.invalidateQueries(messagingQueryKeys.channels());
            } else if (channelExistsInList) {
              // Channel is in list - just update its timestamp and move to top
              // console.log('[Socket] Updating channel', channelId, 'timestamp and moving to top');
              try {
                updateChannelLastMessageAt(
                  queryClient,
                  channelId,
                  messageTimestamp
                );
              } catch (e) {
                console.error('[Socket] Failed to update last-message-at:', e);
              }
            } else {
              // Channel list queries exist but channel not in them (admin not a member)
              // Fetch and add it with correct timestamp
              // console.log('[Socket] Channel', channelId, 'not in cache - fetching and adding');
              try {
                const channelResponse = await queryClient.fetchQuery({
                  queryKey: messagingQueryKeys.channel(channelId),
                  queryFn: async () =>
                    axios.get(`${MESSAGING_API_BASE}/channels/${channelId}`),
                });
                if (channelResponse) {
                  const channelWithDefaults = {
                    ...channelResponse,
                    'read-only': channelResponse['read-only'] ?? false,
                    // Use the message timestamp, not the server's possibly stale one
                    'last-message-at': messageTimestamp,
                  };
                  addNewChannelToCache(queryClient, channelWithDefaults);
                  // console.log('[Socket] Successfully added channel', channelId, 'to cache');
                }
              } catch (e) {
                console.error('[Socket] Failed to fetch/add channel:', e);
              }
            }
          }
        } catch (e) {
          console.error('[Socket] Error during message cache upsert:', e);
        }
      } catch (e) {
        console.error('[Socket] Error in MESSAGING_NEW_MESSAGE handler:', e);
      }
    });
    // Handle message edits
    socket.on('MESSAGING_MESSAGE_EDITED', async (data) => {
      try {
        // console.log('[Socket] MESSAGING_MESSAGE_EDITED received:', data);
        const channelId = Number(data.channelId);
        const messageId =
          data.messageId ||
          (data.message && (data.message.id || data.message['message-id']));
        if (!channelId || !messageId) return;

        // Check if this message is currently being mutated by the current user
        // If so, skip the socket update to avoid overwriting the optimistic update
        const messagingStore =
          getRegisteredMessagingStore && getRegisteredMessagingStore();
        if (messagingStore) {
          const isPending = messagingStore
            .getState()
            .isPendingMutation(messageId);
          if (isPending) {
            // Skip socket update - the mutation's onSuccess will invalidate
            // console.log('[Socket] Skipping update - message', messageId, 'is being mutated by current user');
            return;
          }

          // Mark this channel as having updates so ChatWindow refetches when user navigates back
          const state = messagingStore.getState();
          if (state && typeof state.setSocketData === 'function') {
            const beforeUpdate = state.socketData?.newMessages || [];
            state.setSocketData((prev) => {
              const existing = prev.newMessages || [];
              if (!existing.includes(channelId)) {
                const updated = { newMessages: [...existing, channelId] };
                // console.log('[Socket] Added channel', channelId, 'to newMessages list:', updated.newMessages);
                return updated;
              }
              // console.log('[Socket] Channel', channelId, 'already in newMessages list:', existing);
              return prev;
            });
            const afterUpdate =
              messagingStore.getState().socketData?.newMessages || [];
            // console.log('[Socket] socketData.newMessages after update:', afterUpdate);
          } else {
            console.warn(
              '[Socket] Cannot set socketData - state or setSocketData not available'
            );
          }
        }

        // Update ALL cached message queries for this channel immediately
        // This ensures the edit appears instantly even when the user is on another page

        // Debug: Log all matching queries
        const allQueries = queryClient.getQueryCache().getAll();
        const matchingQueries = allQueries.filter((query) => {
          const key = query.queryKey || [];
          return (
            key?.[0] === 'messaging' &&
            key.some((k) => k === 'messages') &&
            key.some((k) => k === channelId)
          );
        });
        // console.log('[Socket] Found', matchingQueries.length, 'matching queries for channel', channelId);
        matchingQueries.forEach((q) => {
          // console.log('[Socket] Query key:', JSON.stringify(q.queryKey), 'state:', q.state.status);
        });

        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const key = query.queryKey || [];
              return (
                key?.[0] === 'messaging' &&
                key.some((k) => k === 'messages') &&
                key.some((k) => k === channelId)
              );
            },
          },
          (old) => {
            if (!old) return old;

            // Handle infinite query shape
            if (old.pages && Array.isArray(old.pages)) {
              // console.log('[Socket] Updating infinite query cache with', old.pages.length, 'pages');
              let messageFound = false;
              const updatedPages = old.pages.map((page, pageIndex) =>
                Array.isArray(page)
                  ? page.map((msg) => {
                      const msgId = msg.id || msg['message-id'];
                      if (String(msgId) === String(messageId)) {
                        messageFound = true;
                        // console.log('[Socket] Found message in page', pageIndex, '- Current structure:', JSON.stringify(msg, null, 2));

                        // Extract new content from socket data - try multiple paths
                        // Backend sends newContent field for edits
                        const newContent =
                          data.newContent || // ← Primary field from backend
                          data.content ||
                          (data.message && data.message.content) ||
                          (data.message &&
                            data.message.text &&
                            data.message.text.body) ||
                          (data.text && data.text.body) ||
                          '';

                        // console.log('[Socket] Updating message ID:', messageId, 'New content:', newContent, 'Old content:', msg.text?.body);

                        // Only update if we got new content from socket
                        if (!newContent && newContent !== '') {
                          console.warn(
                            '[Socket] No content found in socket data for message',
                            messageId,
                            'data:',
                            data
                          );
                          return msg; // Don't update if we can't extract content
                        }

                        const updated = {
                          ...msg,
                          text: {
                            ...(msg.text || {}),
                            body: newContent,
                          },
                          isEdited: true,
                          'is-edited': true,
                          editedAt:
                            data.editedAt || data.dateUpdated || Date.now(),
                          'date-updated':
                            data.updatedAt ||
                            data.dateUpdated ||
                            new Date().toISOString(),
                        };

                        // console.log('[Socket] Updated message structure:', JSON.stringify(updated, null, 2));
                        return updated;
                      }
                      return msg;
                    })
                  : page
              );

              if (!messageFound) {
                console.warn(
                  '[Socket] Message',
                  messageId,
                  'NOT FOUND in any of the',
                  old.pages.length,
                  'cached pages'
                );
              }

              const result = { ...old, pages: updatedPages };
              // console.log('[Socket] Returning updated infinite query data');
              return result;
            }

            // Handle plain array shape
            if (Array.isArray(old)) {
              // console.log('[Socket] Updating plain array cache with', old.length, 'messages');
              return old.map((msg) => {
                const msgId = msg.id || msg['message-id'];
                if (String(msgId) === String(messageId)) {
                  // Extract new content from socket data - try multiple paths
                  // Backend sends newContent field for edits
                  const newContent =
                    data.newContent || // ← Primary field from backend
                    data.content ||
                    (data.message && data.message.content) ||
                    (data.message &&
                      data.message.text &&
                      data.message.text.body) ||
                    (data.text && data.text.body) ||
                    '';

                  // console.log('[Socket] Updating message ID:', messageId, 'New content:', newContent, 'Old content:', msg.text?.body);

                  // Only update if we got new content from socket
                  if (!newContent && newContent !== '') {
                    console.warn(
                      '[Socket] No content found in socket data for message',
                      messageId,
                      'data:',
                      data
                    );
                    return msg; // Don't update if we can't extract content
                  }

                  return {
                    ...msg,
                    text: {
                      ...(msg.text || {}),
                      body: newContent,
                    },
                    isEdited: true,
                    'is-edited': true,
                    editedAt: data.editedAt || data.dateUpdated || Date.now(),
                    'date-updated':
                      data.updatedAt ||
                      data.dateUpdated ||
                      new Date().toISOString(),
                  };
                }
                return msg;
              });
            }

            return old;
          }
        );

        // console.log('[Socket] Cache update complete for message edit - no invalidation (ChatWindow will refetch on navigation)');

        // Check if channel exists in channels list cache
        const channelsListQueriesEdit = queryClient
          .getQueryCache()
          .getAll()
          .filter((query) => {
            const key = query.queryKey || [];
            return (
              Array.isArray(key) &&
              key.length >= 2 &&
              key[0] === 'messaging' &&
              key[1] === 'channels'
            );
          });

        // console.log('[Socket EDIT] Found', channelsListQueriesEdit.length, 'channel list queries in cache');

        let channelExistsInListEdit = false;
        if (channelsListQueriesEdit.length > 0) {
          for (const query of channelsListQueriesEdit) {
            const queryData = query.state.data;
            if (queryData && queryData.pages) {
              for (const page of queryData.pages) {
                if (
                  page.rows &&
                  page.rows.some((ch) => String(ch.id) === String(channelId))
                ) {
                  channelExistsInListEdit = true;
                  break;
                }
              }
            }
            if (channelExistsInListEdit) break;
          }
        }

        const editTimestamp =
          data.dateUpdated || data.updatedAt || new Date().toISOString();

        if (channelsListQueriesEdit.length === 0) {
          // No channel list queries - invalidate so they refetch on return
          // console.log('[Socket EDIT] No channel list queries - invalidating');
          queryClient.invalidateQueries(messagingQueryKeys.channels());
        } else if (channelExistsInListEdit) {
          // Channel is in list - update timestamp and move to top
          // console.log('[Socket EDIT] Updating channel', channelId, 'and moving to top');
          try {
            updateChannelLastMessageAt(queryClient, channelId, editTimestamp);
          } catch (e) {}
        } else {
          // Channel not in list (admin not member) - fetch and add with correct timestamp
          // console.log('[Socket EDIT] Fetching and adding channel', channelId);
          try {
            const channelResp = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${channelId}`),
            });
            if (channelResp) {
              addNewChannelToCache(queryClient, {
                ...channelResp,
                'read-only': channelResp['read-only'] ?? false,
                'last-message-at': editTimestamp,
              });
            }
          } catch (e) {
            console.error('[Socket EDIT] Failed to fetch/add:', e);
          }
        }
      } catch (e) {}
    });

    // Handle message deletions
    socket.on('MESSAGING_MESSAGE_DELETED', async (data) => {
      try {
        // console.log('[Socket] MESSAGING_MESSAGE_DELETED received:', data);
        const channelId = Number(data.channelId);
        const messageId =
          data.messageId ||
          (data.message && (data.message.id || data.message['message-id']));
        if (!channelId || !messageId) return;

        // Check if this message is currently being mutated by the current user
        // If so, skip the socket update to avoid overwriting the optimistic update
        const messagingStore =
          getRegisteredMessagingStore && getRegisteredMessagingStore();
        if (messagingStore) {
          const isPending = messagingStore
            .getState()
            .isPendingMutation(messageId);
          if (isPending) {
            // Skip socket update - the mutation's onSuccess will invalidate
            // console.log('[Socket] Skipping delete update - message', messageId, 'is being mutated by current user');
            return;
          }

          // Mark this channel as having updates so ChatWindow refetches when user navigates back
          const state = messagingStore.getState();
          if (state && typeof state.setSocketData === 'function') {
            state.setSocketData((prev) => {
              const existing = prev.newMessages || [];
              if (!existing.includes(channelId)) {
                const updated = { newMessages: [...existing, channelId] };
                // console.log('[Socket] Added channel', channelId, 'to newMessages list (delete):', updated.newMessages);
                return updated;
              }
              // console.log('[Socket] Channel', channelId, 'already in newMessages list (delete):', existing);
              return prev;
            });
            const afterUpdate =
              messagingStore.getState().socketData?.newMessages || [];
            // console.log('[Socket] socketData.newMessages after delete update:', afterUpdate);
          } else {
            console.warn(
              '[Socket] Cannot set socketData for delete - state or setSocketData not available'
            );
          }
        }

        // Update ALL cached message queries for this channel immediately
        // This ensures the deletion appears instantly even when the user is on another page
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const key = query.queryKey || [];
              return (
                key?.[0] === 'messaging' &&
                key.some((k) => k === 'messages') &&
                key.some((k) => k === channelId)
              );
            },
          },
          (old) => {
            if (!old) return old;

            // Handle infinite query shape
            if (old.pages && Array.isArray(old.pages)) {
              const updatedPages = old.pages.map((page) =>
                Array.isArray(page)
                  ? page.map((msg) => {
                      const msgId = msg.id || msg['message-id'];
                      if (String(msgId) === String(messageId)) {
                        // console.log('[Socket] Deleting message ID:', messageId, 'Old content:', msg.text?.body);
                        return {
                          ...msg,
                          isDeleted: true,
                          'is-deleted': true,
                          text: msg.text
                            ? {
                                ...msg.text,
                                body: 'This message was deleted',
                              }
                            : { body: 'This message was deleted' },
                          deletedAt:
                            data.deletedAt || data.dateUpdated || Date.now(),
                          'date-updated':
                            data.updatedAt ||
                            data.dateUpdated ||
                            new Date().toISOString(),
                        };
                      }
                      return msg;
                    })
                  : page
              );
              return { ...old, pages: updatedPages };
            }

            // Handle plain array shape
            if (Array.isArray(old)) {
              return old.map((msg) => {
                const msgId = msg.id || msg['message-id'];
                if (String(msgId) === String(messageId)) {
                  // console.log('[Socket] Deleting message ID:', messageId, 'Old content:', msg.text?.body);
                  return {
                    ...msg,
                    isDeleted: true,
                    'is-deleted': true,
                    text: msg.text
                      ? {
                          ...msg.text,
                          body: 'This message was deleted',
                        }
                      : { body: 'This message was deleted' },
                    deletedAt: data.deletedAt || data.dateUpdated || Date.now(),
                    'date-updated':
                      data.updatedAt ||
                      data.dateUpdated ||
                      new Date().toISOString(),
                  };
                }
                return msg;
              });
            }

            return old;
          }
        );

        // console.log('[Socket] Cache update complete for message deletion - no invalidation (ChatWindow will refetch on navigation)');

        // Check if channel exists in channels list cache
        const channelsListQueriesDel = queryClient
          .getQueryCache()
          .getAll()
          .filter((query) => {
            const key = query.queryKey || [];
            return (
              Array.isArray(key) &&
              key.length >= 2 &&
              key[0] === 'messaging' &&
              key[1] === 'channels'
            );
          });

        // console.log('[Socket DELETE] Found', channelsListQueriesDel.length, 'channel list queries in cache');

        let channelExistsInListDel = false;
        if (channelsListQueriesDel.length > 0) {
          for (const query of channelsListQueriesDel) {
            const queryData = query.state.data;
            if (queryData && queryData.pages) {
              for (const page of queryData.pages) {
                if (
                  page.rows &&
                  page.rows.some((ch) => String(ch.id) === String(channelId))
                ) {
                  channelExistsInListDel = true;
                  break;
                }
              }
            }
            if (channelExistsInListDel) break;
          }
        }

        const deleteTimestamp =
          data.dateUpdated || data.updatedAt || new Date().toISOString();

        if (channelsListQueriesDel.length === 0) {
          // No channel list queries - invalidate so they refetch on return
          // console.log('[Socket DELETE] No channel list queries - invalidating');
          queryClient.invalidateQueries(messagingQueryKeys.channels());
        } else if (channelExistsInListDel) {
          // Channel is in list - update timestamp and move to top
          // console.log('[Socket DELETE] Updating channel', channelId, 'and moving to top');
          try {
            updateChannelLastMessageAt(queryClient, channelId, deleteTimestamp);
          } catch (e) {}
        } else {
          // Channel not in list (admin not member) - fetch and add with correct timestamp
          // console.log('[Socket DELETE] Fetching and adding channel', channelId);
          try {
            const channelResp = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${channelId}`),
            });
            if (channelResp) {
              addNewChannelToCache(queryClient, {
                ...channelResp,
                'read-only': channelResp['read-only'] ?? false,
                'last-message-at': deleteTimestamp,
              });
            }
          } catch (e) {
            console.error('[Socket DELETE] Failed to fetch/add:', e);
          }
        }
      } catch (e) {}
    });

    // Handle channel updates (rename, archive, member changes, etc.)
    // Move core channel update handling here so it works even when MyConversations is unmounted
    socket.on('MESSAGING_CHANNEL_UPDATED', async (data) => {
      try {
        if (!data || !data.channelId) return;
        // console.log('[Socket] MESSAGING_CHANNEL_UPDATED received:', data);

        // Handle specific event types similar to useMessagingSubscriptions
        if (data.eventType === 'archived') {
          // Remove from cache
          removeChannelFromCache(queryClient, data.channelId);
        } else if (data.eventType === 'unarchived') {
          try {
            const channelResponse = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(data.channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${data.channelId}`),
            });
            if (channelResponse) {
              const channelWithDefaults = {
                ...channelResponse,
                'read-only': channelResponse['read-only'] ?? false,
                'last-message-at':
                  channelResponse['last-message-at'] ||
                  new Date().toISOString(),
              };
              addNewChannelToCache(queryClient, channelWithDefaults);
            }
          } catch (e) {
            console.error(
              '[Socket] Failed to fetch channel after unarchive:',
              e
            );
            queryClient.invalidateQueries(messagingQueryKeys.channels());
          }
        } else if (
          data.eventType === 'created' ||
          data.eventType === 'member_added'
        ) {
          // New channel created or membership changed - fetch and add/update channel in cache
          try {
            const channelResponse = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(data.channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${data.channelId}`),
            });
            if (channelResponse) {
              const channelWithDefaults = {
                ...channelResponse,
                'read-only': channelResponse['read-only'] ?? false,
                'last-message-at':
                  channelResponse['last-message-at'] ||
                  new Date().toISOString(),
              };
              // Add to top of channels list
              addNewChannelToCache(queryClient, channelWithDefaults);
            }
          } catch (e) {
            console.error(
              '[Socket] Failed to fetch channel after create/member_added:',
              e
            );
            queryClient.invalidateQueries(messagingQueryKeys.channels());
          }
        } else if (data.eventType === 'renamed') {
          try {
            const channelResponse = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(data.channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${data.channelId}`),
            });
            if (channelResponse) {
              updateChannelInCache(
                queryClient,
                data.channelId,
                channelResponse
              );
            }
          } catch (e) {
            console.error('[Socket] Failed to fetch channel after rename:', e);
            queryClient.invalidateQueries(messagingQueryKeys.channels());
          }
        } else if (data.data) {
          // Generic data payload - update channel in place
          updateChannelInCache(queryClient, data.channelId, data.data);
          if (data.data['last-message-at']) {
            updateChannelLastMessageAt(
              queryClient,
              data.channelId,
              data.data['last-message-at']
            );
          }
        }

        // If update was made by current user, don't show indicator
        const { loggedUser } = this.getCurrentProps();
        const currentUserId = loggedUser?.data?.id;
        if (data.userId !== currentUserId) {
          const messagingStore =
            getRegisteredMessagingStore && getRegisteredMessagingStore();
          if (
            messagingStore &&
            typeof messagingStore.getState().setSocketData === 'function'
          ) {
            // add to channelUpdates
            messagingStore.getState().setSocketData((prev) => ({
              channelUpdates: [...(prev.channelUpdates || []), data.channelId],
            }));
          } else if (
            messagingStore &&
            typeof messagingStore.getState === 'function'
          ) {
            // fallback: set via setSocketData if available
            try {
              messagingStore.getState().setSocketData((prev) => ({
                channelUpdates: [
                  ...(prev.channelUpdates || []),
                  data.channelId,
                ],
              }));
            } catch (e) {}
          }
        }
      } catch (e) {
        console.error('[Socket] Error handling MESSAGING_CHANNEL_UPDATED:', e);
      }
    });

    // Handle unread count updates from backend
    // This must be in global.js (not useMessagingSubscriptions) so it works even when MyConversations is unmounted
    socket.on('MESSAGING_UNREAD_COUNT', (data) => {
      try {
        if (!data || !data.channelId) return;

        // console.log('[Socket] MESSAGING_UNREAD_COUNT received:', data);

        // Update the unread counts cache with the canonical count from backend
        queryClient.setQueryData(messagingQueryKeys.unreadCounts(), (old) => {
          const prev = old && typeof old === 'object' ? { ...old } : {};
          prev[String(data.channelId)] = data.unreadCount;
          // console.log('[Socket] Updated unread count for channel', data.channelId, 'to', data.unreadCount);
          return prev;
        });
      } catch (e) {
        console.error('[Socket] Error handling MESSAGING_UNREAD_COUNT:', e);
      }
    });

    // Handle new channel created - ensure it appears immediately in channels list
    socket.on('MESSAGING_CHANNEL_CREATED', async (data) => {
      try {
        if (!data) return;
        // If backend provided full channel payload, add directly
        if (data.channel) {
          const channelWithReadOnly = {
            ...data.channel,
            'read-only': data.channel['read-only'] ?? false,
            'last-message-at':
              data.channel['last-message-at'] || new Date().toISOString(),
          };
          addNewChannelToCache(queryClient, channelWithReadOnly);
          return;
        }

        // Otherwise, fetch channel details and add
        if (data.channelId) {
          try {
            const channelResponse = await queryClient.fetchQuery({
              queryKey: messagingQueryKeys.channel(data.channelId),
              queryFn: async () =>
                axios.get(`${MESSAGING_API_BASE}/channels/${data.channelId}`),
            });
            if (channelResponse) {
              const channelWithDefaults = {
                ...channelResponse,
                'read-only': channelResponse['read-only'] ?? false,
                'last-message-at':
                  channelResponse['last-message-at'] ||
                  new Date().toISOString(),
              };
              addNewChannelToCache(queryClient, channelWithDefaults);
            }
          } catch (e) {
            console.error('[Socket] Failed to fetch channel after create:', e);
            queryClient.invalidateQueries(messagingQueryKeys.channels());
          }
        }
      } catch (e) {
        console.error('[Socket] Error handling MESSAGING_CHANNEL_CREATED:', e);
      }
    });
  }

  componentWillUnmount() {
    const { socket } = this.props;
    // Clean up all socket listeners to prevent memory leaks and duplicate registrations
    socket.off('FRONT_TIMELOG_SETTING');
    socket.off('FRONT_PROJECT_UPDATE');
    socket.off('REPORT_UPDATE_UPDATE');
    socket.off('MESSAGING_NEW_MESSAGE');
    socket.off('MESSAGING_MESSAGE_EDITED');
    socket.off('MESSAGING_MESSAGE_DELETED');
    socket.off('MESSAGING_UNREAD_COUNT');
    socket.off('MESSAGING_CHANNEL_UPDATED');
    socket.off('MESSAGING_CHANNEL_CREATED');
  }

  getCurrentProps() {
    return this.props;
  }
  render() {
    return <div> </div>;
  }
}
